// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { responseMetadataSchema } from '@/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * Schema for a workflow item in API responses
 */
const workflowStagesSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  orderIndex: z.number(),
  inUseCount: z.number().nullable(),
  createdAt: dateTimeSchema,
  updatedAt: dateTimeSchema,
});

/**
 * Schema for a workflow in API responses
 */
export const workflowSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  isDefault: z.boolean(),
  stages: z.array(workflowStagesSchema),
  createdAt: dateTimeSchema,
  updatedAt: dateTimeSchema,
});

/**
 * Schema for the workflows list API response
 */
export const workflowsResponseSchema = z.object({
  data: z.array(workflowSchema),
  metadata: responseMetadataSchema,
});

/**
 * Schema for a single workflow API response
 */
export const workflowResponseSchema = z.object({
  data: workflowSchema,
  metadata: responseMetadataSchema,
});

/**
 * Types derived from the schemas
 */

export type Workflow = z.infer<typeof workflowSchema>;

/**
 * Schema for creating a new workflow with stages (matches backend requirements)
 */
export const createWorkflowWithStagesRequestSchema = z.object({
  name: z.string().min(1, 'Workflow name is required'),
  isDefault: z.boolean().optional(),
  stages: z
    .array(
      z.object({
        name: z.string().min(1, 'Stage name is required'),
        orderIndex: z.number().min(1, 'Order index must be greater than 0'),
      })
    )
    .min(1, 'At least one stage is required'),
});

/**
 * Schema for updating a workflow stage within an update request
 */
const updateWorkflowStageInRequestSchema = z.object({
  id: z.string().optional(), // Optional for new items
  name: z.string().min(1, 'Stage name is required'),
  orderIndex: z.number().min(1, 'Order index must be greater than 0'),
  shouldDelete: z.boolean().optional().default(false),
  migrateTo: z.string().nullable().optional(), // Must be set if shouldDelete is true
});

/**
 * Schema for updating a workflow
 */
export const updateWorkflowRequestSchema = z.object({
  name: z.string().min(1, 'Workflow name is required').optional(),
  isDefault: z.boolean().optional(),
  stages: z.array(updateWorkflowStageInRequestSchema).optional(),
});

/**
 * Types for request data
 */
export type UpdateWorkflowRequest = z.infer<typeof updateWorkflowRequestSchema>;
export type UpdateWorkflowStageInRequest = z.infer<
  typeof updateWorkflowStageInRequestSchema
>;

export type CreateWorkflowWithStagesRequest = z.infer<
  typeof createWorkflowWithStagesRequestSchema
>;
